import React, { useCallback, useEffect, useState } from 'react';
import { Table, Tag, Button, Popconfirm, Empty, Space } from 'antd';
import { SendOutlined, InboxOutlined, CheckOutlined, StopOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { HAB, can, formatMontant } from '../variables/constants';
import { echangesPartenaires, ETATS_ECHANGE, NATURES_ECHANGE, libelleRole } from '../services/echangesPartenaires';
import { destinataireGarantie } from '../services/partieGarantie';
import EnvoiPartenaireModal from './EnvoiPartenaireModal';
import ReponseRecueModal from './ReponseRecueModal';

const GREEN = '#1a6b3c';
const dateFr = (d) => (d ? dayjs(d).format('DD/MM/YYYY') : '-');

/**
 * Échanges du dossier avec ses partenaires : ce qui a été envoyé à la compagnie et au
 * bureau homologue, ce qu'on attend d'eux, ce qu'on a reçu. On y envoie, et on y
 * enregistre la réponse qui clôt une attente.
 */
export default function EchangesPartenairesPanel({ sinistre }) {
  const { t } = useTranslation('commun');
  const [lignes, setLignes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [envoiOuvert, setEnvoiOuvert] = useState(false);
  const [reponsePour, setReponsePour] = useState(null);
  const id = sinistre?.sinistreTrackingId;

  const charger = useCallback(async () => {
    if (!id) return;
    setChargement(true);
    try {
      const res = await echangesPartenaires.dossier(id);
      setLignes(res.data?.data || []);
    } catch {
      setLignes([]);
    } finally {
      setChargement(false);
    }
  }, [id]);

  useEffect(() => { charger(); }, [charger]);

  const clore = async (l) => {
    try {
      await echangesPartenaires.cloreAttente(l.courrierTrackingId);
      toast.success(t('echangesPartenairesPanel.plusAucuneReponse'));
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('echangesPartenairesPanel.erreur'));
    }
  };

  const peutEcrire = can(HAB.COURRIER_CREATE);
  const colonnes = [
    { title: t('echangesPartenairesPanel.colonnes.date'), dataIndex: 'date', key: 'date', width: 105, render: dateFr },
    {
      title: t('echangesPartenairesPanel.colonnes.partenaire'), key: 'partenaire', width: 220,
      render: (_, r) => (
        <span>
          <strong>{r.organisme || '-'}</strong>
          <div style={{ fontSize: 11.5, color: '#6b7280' }}>{libelleRole(r.role)}</div>
        </span>
      ),
    },
    {
      title: t('echangesPartenairesPanel.colonnes.sens'), dataIndex: 'sens', key: 'sens', width: 90,
      render: (v) => (v === 'ENVOYE'
        ? <span><SendOutlined style={{ color: GREEN }} /> {t('echangesPartenairesPanel.envoye')}</span>
        : <span><InboxOutlined style={{ color: '#2563eb' }} /> {t('echangesPartenairesPanel.recu')}</span>),
    },
    { title: t('echangesPartenairesPanel.colonnes.nature'), dataIndex: 'nature', key: 'nature', width: 150, render: (v) => NATURES_ECHANGE[v] || v || '-' },
    { title: t('echangesPartenairesPanel.colonnes.objet'), dataIndex: 'objet', key: 'objet', ellipsis: true },
    {
      title: t('echangesPartenairesPanel.colonnes.etat'), dataIndex: 'etat', key: 'etat', width: 175,
      render: (v, r) => {
        const e = ETATS_ECHANGE[v] || { label: v, color: 'default' };
        return (
          <span>
            <Tag color={e.color} style={{ borderRadius: 12 }}>{e.label}</Tag>
            {(v === 'EN_ATTENTE' || v === 'EN_RETARD') && r.echeance && (
              <div style={{ fontSize: 11.5, color: '#6b7280' }}>{t('echangesPartenairesPanel.echeance', { date: dateFr(r.echeance) })}</div>
            )}
            {v === 'REPONDU' && <div style={{ fontSize: 11.5, color: '#6b7280' }}>{t('echangesPartenairesPanel.repondLe', { date: dateFr(r.dateReponse) })}</div>}
            {r.montant != null && <div style={{ fontSize: 11.5 }}>{formatMontant(r.montant)}</div>}
          </span>
        );
      },
    },
    {
      title: '', key: 'actions', width: 210, align: 'right',
      render: (_, r) => ((r.etat === 'EN_ATTENTE' || r.etat === 'EN_RETARD') && r.courrierTrackingId && peutEcrire ? (
        <Space size={4}>
          <Button size="small" icon={<CheckOutlined />} onClick={() => setReponsePour(r)}>{t('echangesPartenairesPanel.reponseRecue')}</Button>
          <Popconfirm title={t('echangesPartenairesPanel.confirmerClore')} okText={t('echangesPartenairesPanel.oui')} cancelText={t('echangesPartenairesPanel.non')} onConfirm={() => clore(r)}>
            <Button size="small" type="text" icon={<StopOutlined />} title={t('echangesPartenairesPanel.plusAttendue')} />
          </Popconfirm>
        </Space>
      ) : null),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: '#4b5563' }}>
          {t('echangesPartenairesPanel.partenaireDuDossier', { destinataire: destinataireGarantie(sinistre) })}
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={charger} />
          {peutEcrire && (
            <Button type="primary" icon={<SendOutlined />} onClick={() => setEnvoiOuvert(true)}
              style={{ background: GREEN, borderColor: GREEN }}>
              {t('echangesPartenairesPanel.envoyer')}
            </Button>
          )}
        </Space>
      </div>
      <Table
        className="cb-table" size="small" loading={chargement} dataSource={lignes} columns={colonnes}
        rowKey={(r, i) => r.courrierTrackingId || `${r.reference}-${i}`} pagination={false} scroll={{ x: 1000 }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('echangesPartenairesPanel.aucunEchange')} /> }}
      />
      <EnvoiPartenaireModal open={envoiOuvert} onClose={() => setEnvoiOuvert(false)} sinistre={sinistre} onEnvoye={charger} />
      <ReponseRecueModal open={!!reponsePour} ligne={reponsePour} onClose={() => setReponsePour(null)} onEnregistre={charger} />
    </div>
  );
}
